import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { usePermissions, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    Claims,
    CreateMutation,
    DefaultPagedResponse,
    DeleteMutation,
    Entity,
    FormSelect,
    FormSelectOption,
    IUseQueryPaged,
    MutationResult,
    PagedResponse,
    QueryHookParams,
    QueryResult,
    QueryType,
} from "../models";
import { Operation } from "rfc6902";

export interface OptionUpdateMutationData {
    operations: Operation[];
    optionId: number;
}

export interface IUseFormSelectQuery extends IUseQueryPaged<FormSelect> {
    uploadMutation: MutationResult<FormSelect[], unknown, FormData, FormSelect[]>;
    getBySlugQuery: QueryResult<FormSelect>;
    addOptionMutation: CreateMutation<FormSelectOption>;
    updateOptionMutation: MutationResult<FormSelectOption, unknown, OptionUpdateMutationData>;
    deleteOptionMutation: DeleteMutation;
}

interface IUseFormSelectQueryParams extends QueryHookParams {
    slug?: string;
}

export const useFormSelectQuery = (params?: IUseFormSelectQueryParams): IUseFormSelectQuery => {
    const formSelectId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { hasPermission } = usePermissions();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.FormSelects);
    const url = Endpoints.FormSelects;
    const optionsUrl = Endpoints.FormSelectOptions;
    const queryType = params?.slug ? QueryType.Detail : getQueryType(formSelectId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<FormSelect>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.formSelects.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: hasPermission(Claims.ViewFormSelect) && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<FormSelect>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, true)
            ).then((response) => response.items),
        queryKey: queryKeys.formSelects.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: hasPermission(Claims.ViewFormSelect) && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<FormSelect>({ url: `${url}/${formSelectId}`, config }),
        queryKey: queryKeys.formSelects.detail(formSelectId),
        placeholderData: undefined,
        enabled: hasPermission(Claims.ViewFormSelect) && queryType === QueryType.Detail && !!params?.id,
    });

    const getBySlugQuery = useGenericQuery({
        queryFn: (config) => get<FormSelect>({ url: `${url}/slug/${params?.slug}`, config }),
        queryKey: queryKeys.formSelects.detail(params?.slug ?? ""),
        placeholderData: undefined,
        enabled: queryType === QueryType.Detail && !!params?.slug,
        cacheTime: 60 * 60 * 1000, // 1 hour
        staleTime: 60 * 60 * 1000, // 1 hour
    });

    const uploadMutation = useGenericMutation({
        mutationFn: (data: FormData) => post<FormData, FormSelect[]>({ url: `${url}/upload`, data }),
        queryKey: queryKeys.formSelects.all(),
        keysToInvalidate: [
            queryKeys.formSelects.all(),
            queryKeys.formSelects.lists(),
            queryKeys.formSelects.pagedLists(),
            queryKeys.formCategories.all(),
            queryKeys.formCategories.lists(),
            queryKeys.formCategories.pagedLists(),
        ],
        successToast: "Form Dropdowns created",
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<FormSelect>) => post<Partial<FormSelect>, FormSelect>({ url, data }),
        queryKey: queryKeys.formSelects.all(),
        keysToInvalidate: [queryKeys.formSelects.lists(), queryKeys.formSelects.pagedLists()],
        successToast: "Form Dropdown created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], FormSelect>({ url: `${url}/${formSelectId}`, data }),
        queryKey: queryKeys.formSelects.detail(formSelectId),
        keysToInvalidate: [queryKeys.formSelects.lists(), queryKeys.formSelects.pagedLists()],
        successToast: "Form Dropdown updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${formSelectId}` }),
        queryKey: queryKeys.formSelects.detail(formSelectId),
        keysToInvalidate: [queryKeys.formSelects.lists(), queryKeys.formSelects.pagedLists()],
        successToast: "Form Dropdown deleted",
    });

    const addOptionMutation = useGenericMutation({
        mutationFn: (data: Partial<FormSelectOption>) =>
            post<Partial<FormSelectOption>, FormSelectOption>({ url: optionsUrl, data }),
        queryKey: queryKeys.formSelects.detail(formSelectId),
        keysToInvalidate: [
            queryKeys.formSelects.lists(),
            queryKeys.formSelects.pagedLists(),
            queryKeys.formSelects.details(),
        ],
        successToast: "Form Dropdown Option created",
    });

    const updateOptionMutation = useGenericMutation({
        mutationFn: (data: OptionUpdateMutationData) =>
            patch<Operation[], FormSelectOption>({ url: `${optionsUrl}/${data.optionId}`, data: data.operations }),
        queryKey: queryKeys.formSelects.detail(formSelectId),
        keysToInvalidate: [
            queryKeys.formSelects.lists(),
            queryKeys.formSelects.pagedLists(),
            queryKeys.formSelects.details(),
        ],
        successToast: "Form Dropdown Option updated",
    });

    const deleteOptionMutation = useGenericMutation({
        mutationFn: (optionId: number) => del({ url: `${optionsUrl}/${optionId}` }),
        queryKey: queryKeys.formSelects.detail(formSelectId),
        keysToInvalidate: [
            queryKeys.formSelects.lists(),
            queryKeys.formSelects.pagedLists(),
            queryKeys.formSelects.details(),
        ],
        successToast: "Form Dropdown Option deleted",
    });

    return useMemo<IUseFormSelectQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            getBySlugQuery,
            uploadMutation,
            createMutation,
            updateMutation,
            deleteMutation,
            addOptionMutation,
            updateOptionMutation,
            deleteOptionMutation,
        }),
        [
            pagedListQuery,
            listQuery,
            detailQuery,
            getBySlugQuery,
            uploadMutation,
            createMutation,
            updateMutation,
            deleteMutation,
            addOptionMutation,
            updateOptionMutation,
            deleteOptionMutation,
        ]
    );
};
